import { Component, HostListener, inject, OnInit } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import {
  AlertInlineComponent,
  ToasterService,
  ModalComponent,
} from '@mars/ui-components';
import { ModalService } from './common/services/modal/modal.service';
import { AuthService } from './common/services/auth/auth.service';
import { LanguageService } from './common/services/language/language.service';
import { UserState as UserStateType } from './common/models/user';
import { NavigationPipe } from './common/pipes/navigation/navigation.pipe';
import { Path } from './common/constants/paths.constant';
import { UserState } from './common/constants/userStates.constant';

@Component({
  imports: [RouterOutlet, AlertInlineComponent, ModalComponent],
  selector: 'mars-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  title = 'mars';

  protected readonly toastService = inject(ToasterService);
  protected readonly modalService = inject(ModalService);
  private readonly authService = inject(AuthService);
  private readonly languageService = inject(LanguageService);
  private readonly router = inject(Router);
  private readonly navigationPipe = new NavigationPipe();

  @HostListener('window:beforeunload')
  onBeforeUnload() {
    sessionStorage.clear();
  }

  ngOnInit(): void {
    if (this.authService.isLoggedIn()) this.checkAndUpdateUserState();
  }

  updateUserState(state: UserStateType) {
    const userData = this.authService.userData();
    const userType = this.authService.userType();
    if (!userData) return;

    // Only the status itself gates the redirect below — `label` alone can
    // still differ (e.g. a locale switch re-fetched the same status in the
    // other language), and that refresh must still land in `userData` so
    // the badge stops showing the language it was cached in.
    const statusChanged =
      !!userType?.user &&
      userData.user.state[userType.user]?.name !== state[userType.user]?.name;

    const prevHasState =
      !!userData.user.state?.buyer ||
      !!userData.user.state?.supplier ||
      !!userData.user.state?.contractor;

    userData.user.state = state;
    this.authService.updateUser(userData);

    if (!statusChanged) return;

    const currentHasState =
      !!userData.user.state.buyer ||
      !!userData.user.state.supplier ||
      !!userData.user.state.contractor;

    if (!prevHasState && currentHasState)
      this.router.navigate([
        '/' +
          this.navigationPipe.transform(`${userType?.user}/${Path.DASHBOARD}`),
      ]);

    this.handleNavigation();
  }

  checkAndUpdateUserState() {
    this.authService.getUserState().subscribe(response => {
      if (response?.user) {
        // `updateUserState` below no-ops when the state hasn't changed, which
        // is the common case — so the locale sync can't wait on it.
        this.languageService.syncFromLocale(response.user.locale);
        this.updateUserState(response.user.state);
      }
    });
  }

  handleNavigation() {
    const userType = this.authService.userType();
    if (!userType) return;
    const { user, state } = userType;

    if (user === 'supplier') {
      switch (state) {
        case UserState.APPROVE:
          this.router.navigate([
            '/' +
              this.navigationPipe.transform(
                `${Path.SUPPLIER}/${Path.DASHBOARD}`
              ),
          ]);
          break;
        case UserState.REJECTED:
          this.router.navigate([
            '/' + this.navigationPipe.transform(Path.SUPPLIER_REGISTRATION),
          ]);
          break;
      }
    } else if (user === 'contractor') {
      switch (state) {
        case UserState.APPROVE:
          this.router.navigate([
            '/' +
              this.navigationPipe.transform(
                `${Path.CONTRACTOR}/${Path.DASHBOARD}`
              ),
          ]);
          break;
        case UserState.REJECTED:
          this.router.navigate([
            '/' + this.navigationPipe.transform(Path.CONTRACTOR_REGISTRATION),
          ]);
          break;
        default:
          this.router.navigate([
            '/' +
              this.navigationPipe.transform(
                `${Path.CONTRACTOR}/${Path.DASHBOARD}`
              ),
          ]);
          break;
      }
    }
  }
}
